import bannerValidateActions from '../actions/actions.validation.interactive-banner';
import vehicleActions from '../actions/actions.vehicle';
import { BannerReceivedParams } from '../interfaces/interface.banner';
import { DealerData } from '../interfaces/interface.dealer';
import { VehiclePossibility } from '../interfaces/interface.vehicle';
import { getHealthCheck, getSuggestions } from '../services/service.banner';
import { fetchDealerActiveStatus, fetchDealerInCobrandingGroupStatus } from '../services/service.dealer';
import EventBus from '../services/service.eventBus';
import { fetchStates } from '../services/service.location';
// import { getIsOptimizelyTrue } from '../services/service.optimizely';
import { fetchActiveAutotraderListing, fetchVehiclePossibilities, fetchVehicleTypicalMileage } from '../services/service.vehicle';
import { VehicleEligibility, fetchVehicleEligibility, fetchYearsEligibility } from '../services/services.eligibility';
import { state as bannerState, resetVehicleData as resetBanner } from '../store/store.interactive-banner';
import { trackBannerButtonClick } from '../tracking/tracking.interactiveBanner';
import { ACS_EVENT_PROPS } from '../utils/utils.asc-analytics';
import {
  BannerInstallationMode,
  BannerStep,
  BannerStepStorage,
  DEFAULT_TAB_LIST,
  FunnelUrlParams,
  GUID_EMPTY,
  LEVEL_BUYING_CENTER,
  LEVEL_EXPERT_APPRAISAL,
  SOURCE_KBB,
  STATUS_SUCCESS,
  bannerStatus,
  createFunnelAssignedOfferUrl,
  createFunnelUrl,
  createOfferCertificateUrl,
} from '../utils/utils.interactive-banner';
import { SHIFT_DIGITAL_ANALYTICS_LOAD_SUCCESS_EVENT } from '../utils/utils.shift-digital-analytics';
import { BROWSER_STORAGE_TTL, getFromBrowserStorage, removeFromBrowserStorage, setTtlFromBrowserStorage, writeStoreToBrowserStorage } from '../utils/utils.store';

const onCheckInit = async (groupId, dealerId, dealerGroupId) => {
  if (groupId) {
    await onDealerInCobrandingGroupCheckInit(groupId, dealerId, dealerGroupId);
  }
  if (bannerState.dealerId || dealerId) {
    await onDealerAuthorizedCheckInit();
  }
};

const onDealerAuthorizedCheckInit = async () => {
  try {
    await onDealerAuthorizedSuccess();
  } catch (error) {
    onDealerAuthorizedFail(error);
  }
};

const onDealerInCobrandingGroupCheckInit = async (groupId, dealerId, dealerGroupId) => {
  try {
    await onDealerInCobrandingGroupSuccess(groupId, dealerId, dealerGroupId);
  } catch (error) {
    onDealerInCobrandingGroupFail(error);
  }
};

const extractDealerData = (rawResponse): DealerData => {
  try {
    const dealer: DealerData = {
      dealerGuid: rawResponse['data']['dealerGuid'],
      subscriptionSourceLevels: [],
      svocId: rawResponse['data']['autoTraderID'],
      acceptRv: rawResponse['data']['acceptsReplacementVehicleAnswers'],
    };

    const subscription = rawResponse['data']['subscriptionSourceLevels'];
    if (subscription?.length > 0) {
      subscription.forEach((item) => {
        const { sourceValue, levelValue } = item;
        if (levelValue) {
          dealer.subscriptionSourceLevels.push({ sourceValue, levelValue });
        }
      });
    }

    return dealer;
  } catch (err) {
    console.error('Error extractDealerData', err);

    return null;
  }
};

const onDealerAuthorizedSuccess = async () => {
  EventBus.publish('DealerAuthorizeCheck', bannerState.dealerId);

  let dealerActive = false;
  bannerState.isFetching = true;

  const res = await fetchDealerActiveStatus(bannerState.dealerId);
  onSetShiftDigitalContainer(res);
  bannerState.measurementId = res?.data?.measurementId;

  const dealerData = extractDealerData(res);
  if (dealerData) {
    bannerState.dealershipAcceptsRv = dealerData.acceptRv;
    bannerState.svocId = dealerData.svocId;
    dealerActive =
      dealerData.dealerGuid !== GUID_EMPTY &&
      dealerData.subscriptionSourceLevels.length > 0 &&
      dealerData.subscriptionSourceLevels[0]['sourceValue'] === SOURCE_KBB &&
      (dealerData.subscriptionSourceLevels[0]['levelValue'] === LEVEL_EXPERT_APPRAISAL || dealerData.subscriptionSourceLevels[0]['levelValue'] === LEVEL_BUYING_CENTER);
  }

  EventBus.publish('DealerAuthorizeCheckSuccess', dealerActive);

  if (!dealerActive) {
    bannerState.activeStep = BannerStep.UnSubscribed;
  }
  bannerState.isFetching = false;
};

const onSetShiftDigitalContainer = (res) => {
  const { data } = res;
  if (!data) {
    return;
  }

  const { dealerGroups, displayName } = data;
  if (isShiftDigitalDealer(dealerGroups, ['fca'])) {
    bannerState.sdContainer = 'STELLANTIS';
  }

  if (isShiftDigitalDealer(displayName, ['toyota'])) {
    bannerState.sdContainer = 'TOYOTA';
  }

  if (isShiftDigitalDealer(displayName, ['lexus'])) {
    bannerState.sdContainer = 'LEXUS';
  }

  if (isShiftDigitalDealer(displayName, ['chevrolet', 'buick', 'gmc', 'cadillac'])) {
    bannerState.sdContainer = 'GMUS';
  }

  if (isShiftDigitalDealer(displayName, ['genesis'])) {
    bannerState.sdContainer = 'GENESISUS';
  }

  bannerState.oemDealerCode = data.oemDealerCode;

  if (bannerState.sdContainer && bannerState.oemDealerCode) {
    EventBus.subscribe(SHIFT_DIGITAL_ANALYTICS_LOAD_SUCCESS_EVENT, (event) => {
      bannerState.sessionId = event.tradeInSessionId;
    });
  }
};

const isShiftDigitalDealer = (text, arr) => {
  return text && arr.some((element) => text.toString().toLowerCase().includes(element));
};

const onShiftDigitalRemove = () => {
  if (bannerState.sdContainer && bannerState.oemDealerCode) {
    EventBus.unsubscribe(SHIFT_DIGITAL_ANALYTICS_LOAD_SUCCESS_EVENT, (event) => {
      bannerState.sessionId = event.tradeInSessionId;
    });
  }
};

const onDealerInCobrandingGroupSuccess = async (groupIdValue, dealerIdValue, dealerGroupIdValue) => {
  EventBus.publish('DealerInCobrandingGroupCheck', { dealerId: dealerIdValue, groupId: groupIdValue, dealerGroupId: dealerGroupIdValue });

  let dealerInCobrandingGroup = false;
  bannerState.isFetching = true;
  const dealerId = dealerGroupIdValue ? dealerGroupIdValue : dealerIdValue;
  const res = await fetchDealerInCobrandingGroupStatus(dealerId, groupIdValue);
  if (res?.meta?.codes[0] === STATUS_SUCCESS && (res?.data?.enabled || res?.data?.cobrandingGroup?.enabled)) {
    dealerInCobrandingGroup = true;
    bannerState.dealerId = res?.data?.dealerId;
    bannerState.svocId = res?.data?.autoTraderID;
    bannerState.isRequireVinPlate = res?.data?.cobrandingGroup?.isRequireVinPlate;
    bannerState.cobrandingGroupAcceptsRv = res?.data?.cobrandingGroup?.acceptsReplacementVehicleAnswers;
    bannerState.cobrandingGroupMeasurementId = dealerId ? res?.data?.measurementId : res?.data?.measurementID;
  }

  EventBus.publish('DealerInCobrandingGroupCheckSuccess', dealerInCobrandingGroup);

  if (!dealerInCobrandingGroup) {
    bannerState.activeStep = BannerStep.UnSubscribed;
  }

  bannerState.isFetching = false;
};

const onOptimizelyCheck = () => {
  onOptimizelyCheckSuccess();
};

const onOptimizelyCheckSuccess = () => {
  bannerState.optimizelyBannerIsActive = true;
};

const onHealthCheck = async () => {
  try {
    await onHealthCheckSuccess();
  } catch (error) {
    onHealthCheckFail(error);
  }
};

const onHealthCheckSuccess = async () => {
  EventBus.publish('ServicesHealthCheck', bannerState.dealerId);

  bannerState.isFetching = true;
  const ret = await getHealthCheck();
  bannerState.isServicesHealthy = ret.status === 200;
  bannerState.isFetching = false;

  EventBus.publish('ServicesHealthCheckSuccess', ret);
};

const onHealthCheckFail = (error) => {
  bannerState.isServicesHealthy = false;
  EventBus.publish('ServicesHealthCheckFail', error);
};

const onBannerNextAction = () => {
  if (bannerState.optimizelyBannerIsActive) {
    bannerState.furyUrl = createFunnelUrl(bannerState as FunnelUrlParams);
  } else {
    bannerState.furyUrl = createFunnelAssignedOfferUrl(bannerState as FunnelUrlParams);
  }

  switch (bannerState.installationMode) {
    case BannerInstallationMode.Offsite: {
      window.open(bannerState.furyUrl);
      break;
    }
    case BannerInstallationMode.PopUp: {
      bannerState.isOpenModal = true;
      const urlCurrentStep = {
        urlCurrentStep: bannerState.furyUrl,
      };
      saveCurrentStepToSessionStorage(BannerStepStorage.UrlCurrentStep, urlCurrentStep);
      checkSessionStorageToContinue();
      break;
    }
  }
};

const onModalClose = () => {
  bannerState.isOpenModal = false;
};

const onDealerAuthorizedFail = (error) => {
  bannerState.isFetching = false;
  bannerState.activeStep = BannerStep.UnSubscribed;

  EventBus.publish('DealerAuthorizeCheckFail', error);
};

const onDealerInCobrandingGroupFail = (error) => {
  bannerState.isFetching = false;
  bannerState.activeStep = BannerStep.UnSubscribed;

  EventBus.publish('DealerInCobrandingGroupCheckFail', error);
};

const onFindVehicleByYMM = async (vehicleId) => {
  bannerState.ymmSuggestions = [];
  bannerState.isFetching = true;

  const eligible = await checkVehicleEligibility(vehicleId);
  if (eligible) {
    bannerState.vin = '';
    bannerState.activeStep = BannerStep.MSM;
  } else {
    bannerState.activeStep = BannerStep.NotQualify;
  }

  bannerState.isFetching = false;
};

const onFindVehicleByLP = async (licensePlateNumber, licensePlateState) => {
  bannerState.isFetching = true;

  let isEligible = false;

  const vins = await vehicleActions.onFetchVinByPlate(licensePlateNumber, licensePlateState);

  let hasError = !vins || vins.length === 0;
  if (!hasError) {
    const isFoundVin = await onFetchVehiclePossibilities(vins[0]);
    hasError = hasError || !isFoundVin;
  }

  if (!hasError && bannerState.vehiclePossibilities.length === 1) {
    isEligible = await checkVehicleEligibility(bannerState.trimId);
  }

  if (hasError) {
    bannerValidateActions.onValidateBannerLP(licensePlateNumber, licensePlateState);
  } else if (!isEligible) {
    bannerState.activeStep = BannerStep.NotQualify;
  } else {
    await onFetchVehicleActiveAutotraderListing(vins[0]);
    bannerState.activeStep = BannerStep.MSM;
  }
  onTrackStartMyOffer(!hasError);

  bannerState.isFetching = false;
};

export const checkVehicleEligibility = async (trimId: number): Promise<boolean> => {
  try {
    return await onFetchVehicleEligibilitySuccess(trimId);
  } catch (error) {
    onFetchVehicleEligibilityFail(error);
    return false;
  }
};

export const onFetchVehicleEligibilitySuccess = async (trimId: number): Promise<boolean> => {
  if (!trimId) {
    return false;
  }

  EventBus.publish('FetchVehicleEligibility', trimId);

  const eligibilityRec: VehicleEligibility = await fetchVehicleEligibility(trimId);
  let isEligible = true;
  if (eligibilityRec.isEligible !== true) {
    isEligible = false;
  }

  EventBus.publish('FetchVehicleEligibilitySuccess', eligibilityRec);

  return isEligible;
};

export const onFetchVehicleEligibilityFail = (error) => {
  EventBus.publish('FetchVehicleEligibilityFail', error);
};

const onFindByVin = async (vin: string): Promise<void> => {
  bannerState.isFetching = true;
  bannerState.vin = vin;
  const isFoundVin = await onFetchVehiclePossibilities(vin);
  onTrackStartMyOffer(isFoundVin);

  if (!isFoundVin) {
    bannerValidateActions.onValidateBannerVin(vin, true);
    bannerState.isFetching = false;
    return;
  }

  let isIneligible = false;
  if (bannerState.vehiclePossibilities?.length === 1) {
    isIneligible = !(await checkVehicleEligibility(bannerState.trimId));
  }

  if (isIneligible) {
    bannerState.activeStep = BannerStep.NotQualify;
  } else {
    await onFetchVehicleActiveAutotraderListing(vin);
    bannerState.activeStep = BannerStep.MSM;
  }

  bannerState.isFetching = false;
};

const onFetchVehiclePossibilities = async (vin: string): Promise<boolean> => {
  try {
    return await onFetchVehiclePossibilitiesSuccess(vin);
  } catch (error) {
    onFetchVehiclePossibilitiesFail(error);
    return false;
  }
};

export const onFetchVehiclePossibilitiesSuccess = async (vin: string): Promise<boolean> => {
  EventBus.publish('FetchVehiclePossibilities', vin);

  let vehicles: VehiclePossibility[] = null;
  bannerState.vin = vin;

  const fetchByVin = await fetchVehiclePossibilities(vin);
  vehicles = fetchByVin.possibilities;
  EventBus.publish('FetchVehiclePossibilitiesSuccess', vin);

  if (vehicles === null || vehicles.length === 0) {
    return false;
  }

  if (!fetchByVin.isAutoCheckEligible) {
    bannerState.activeStep = BannerStep.NotQualify;
    return false;
  }

  const vehicle = vehicles[0];
  bannerState.vehiclePossibilities = buildOptions(vehicles);
  bannerState.yearId = vehicle.year.id;
  bannerState.makeId = parseInt(vehicle.make.id.toString());
  bannerState.make = vehicle.make.displayName.toString();
  bannerState.modelId = vehicle.model.id;
  bannerState.model = vehicle.model.displayName.toString();

  if (vehicles.length === 1) {
    bannerState.trimId = vehicle.trim.id;
    bannerState.trim = vehicle.trim.displayName.toString();
    bannerState.vehicleId = vehicle.trim.id;
  }

  const mileage = fetchByVin.autoCheckData['miles'];
  if (mileage && typeof mileage === 'number') {
    bannerState.autoCheckMiles = mileage;
  }
  return true;
};

const buildOptions = (options: VehiclePossibility[]) => {
  if (!options) {
    return [];
  }

  return options.map((o: VehiclePossibility) => ({
    label: `${o.trim.displayName}`,
    value: `${o.trim.id}`,
  }));
};

export const onFetchVehiclePossibilitiesFail = (error) => {
  EventBus.publish('FetchVehiclePossibilitiesFail', error);
};

const onFetchVehicleActiveAutotraderListing = async (vin: string): Promise<boolean> => {
  try {
    return await onFetchVehicleActiveAutotraderListingSuccess(vin);
  } catch (error) {
    onFetchVehicleActiveAutotraderListingFail(error);
    return false;
  }
};

export const onFetchVehicleActiveAutotraderListingSuccess = async (vin: string): Promise<boolean> => {
  EventBus.publish('FetchVehicleActiveAutotraderListing', vin);

  const listedInfo = await fetchActiveAutotraderListing(vin, bannerState.svocId);
  EventBus.publish('FetchVehicleActiveAutotraderListingSuccess', vin);

  if (!listedInfo) {
    return false;
  }
  bannerState.isActiveListing = !!listedInfo && !!listedInfo.totalResultCount && listedInfo.totalResultCount > 0 && !listedInfo.privateSeller;
  bannerState.activeDealerListingUrl = listedInfo && listedInfo.autoListingUrl ? listedInfo.autoListingUrl : '';
  return true;
};

export const onFetchVehicleActiveAutotraderListingFail = (error) => {
  EventBus.publish('FetchVehicleActiveAutotraderListingFail', error);
};

//  In order to limit calling the server too many times each time a character is entered,
//  this function will delay the call to the api after the user finishes entering the character after 250ms.
let timeout = null;
const onYmmChange = async (textSearch: string) => {
  if (textSearch.slice(-1) === ' ') {
    return await actions.onYmmSuggest(textSearch);
  }
  clearTimeout(timeout);
  timeout = setTimeout(async () => {
    return await actions.onYmmSuggest(textSearch);
  }, 250);
};

const onYmmSuggest = async (search: string) => {
  bannerState.makeId = null;
  bannerState.make = '';
  bannerState.model = '';
  bannerState.modelId = null;
  bannerState.trimId = null;
  bannerState.vehicleId = null;
  bannerState.yearId = null;
  bannerState.ymmSuggestions = [];
  if (!!search) {
    const results = await getSuggestions(search);
    if (results) {
      bannerState.ymmSuggestions = results.filter((r) => r.yearId >= bannerState.yearRange.min && r.yearId <= bannerState.yearRange.max);
    }
    return bannerState.ymmSuggestions?.length > 0;
  }
  return true;
};

const onYmmSelected = (vID) => {
  const selectedSuggestion = bannerState.ymmSuggestions.find((s) => s.vehicleId === vID);
  const { makeId, makeName, modelId, modelName, yearId, vehicleId, trimId, trimName } = selectedSuggestion;
  bannerState.makeId = makeId;
  bannerState.make = makeName;
  bannerState.model = modelName;
  bannerState.modelId = modelId;
  bannerState.trimId = trimId;
  bannerState.trim = trimName;
  bannerState.vehicleId = vehicleId;
  bannerState.yearId = yearId;
  bannerState.ymmSuggestions = [];
};

const onMileageSubmit = async (trimId, mileage) => {
  const isEligible = await checkVehicleEligibility(trimId);
  const hasMultipleTrims = bannerState.vehiclePossibilities.length > 1;

  if (!isEligible) {
    bannerState.activeStep = BannerStep.NotQualify;
    return;
  }

  if (hasMultipleTrims) {
    bannerState.trimId = trimId;
    bannerState.trim = bannerState.vehiclePossibilities.find((v) => v.value === trimId.toString()).label;
    bannerState.vehicleId = trimId;
  }

  bannerState.mileage = mileage;
  bannerState.activeStep = BannerStep.ThankYou;
  onBannerNextAction();
  trackBannerButtonClick(ACS_EVENT_PROPS.NEXT);
};

const updateVinInputStatus = (vin: string) => {
  bannerState.inputVin = vin;
  bannerState.vin = vin;
};

const updateLicensePlateStatus = (licensePlateNumber: string, licensePlateState: string) => {
  bannerState.licensePlateNumber = licensePlateNumber;
  bannerState.licensePlateState = licensePlateState;
};

const updateYMMStatusThenValidate = (vehicleId: number, inputYMM: string, isVehicleEligible: boolean): boolean => {
  bannerState.vehicleId = vehicleId;
  bannerState.inputYmm = inputYMM;
  return bannerValidateActions.onValidateBannerYMM(inputYMM, isVehicleEligible);
};

const updateMileageStatus = (mileage: number) => {
  bannerState.mileage = mileage;
};

const resetBannerState = () => {
  bannerState.vin = '';
  bannerState.inputVin = '';
  bannerState.mileage = 0;
  resetBanner();
  bannerValidateActions.resetBannerValidationState();
  bannerState.mileageRangeValidated = false;
};

const setCurrentSelectedTab = (key: string) => {
  bannerState.currentSelectedTab = key;
};

const VALID_TABS = ['vin', 'lp', 'ymm'];
// const defaultTabList = 'vin-lp-ymm';

const getTabHeaders = (tabList: string) => {
  const tabArray = tabList.split('-');
  const headers = [];
  tabArray.forEach((tab) => {
    if (tab === 'vin') {
      headers.push({
        key: 'vin',
        label: 'VIN',
      });
    }
    if (tab === 'lp') {
      headers.push({
        key: 'licensePlate',
        label: 'License Plate',
      });
    }
    if (tab === 'ymm') {
      headers.push({
        key: 'ymm',
        label: 'Make Model',
      });
    }
  });
  return headers;
};

export const onReceivedParams = (receivedParams: BannerReceivedParams) => {
  const { installationMode, defaultSelectedTab } = receivedParams;
  let { tabList } = receivedParams;

  if (!Object.values(BannerInstallationMode).includes(installationMode)) {
    throw 'Invalid installation-mode.';
  }

  if (bannerState.isRequireVinPlate) {
    tabList = 'vin-lp';
  }

  bannerState.tabList = tabList;
  setTabListToDefaultIfInvalid(tabList);
  displayDefaultTab(defaultSelectedTab);
  assignParamsToState(receivedParams);
};

const setTabListToDefaultIfInvalid = (tabList: string) => {
  const tabArray = getTabArray();
  const isAnyTabInvalid = tabArray.length > 0 ? tabArray.some((tab) => !VALID_TABS.includes(tab)) : true;
  bannerState.tabList = isAnyTabInvalid ? DEFAULT_TAB_LIST : tabList;
};

const getTabArray = () => bannerState.tabList.split('-').filter((tab) => tab !== '');

const isDefaultTabValid = (defaultSelectedTab: string): boolean => {
  const tabArray = getTabArray();
  return tabArray.includes(defaultSelectedTab);
};

const displayDefaultTab = (newDefaultSelectedTab: string) => {
  const tabArray = getTabArray();
  const invalidNewDefaultSelectedTab = !isDefaultTabValid(newDefaultSelectedTab);
  bannerState.defaultSelectedTab = invalidNewDefaultSelectedTab ? tabArray[0] : newDefaultSelectedTab;
  actions.setCurrentSelectedTab(bannerState.defaultSelectedTab);
};

const assignParamsToState = ({ offerCode, dealerId, voiVin, installationMode, zIndex, vin, mileage, groupId, dealerGroupId }) => {
  bannerState.offerCode = offerCode ?? '';
  bannerState.dealerId = dealerId ?? '';
  bannerState.dealerGroupId = dealerGroupId ?? '';
  bannerState.groupId = groupId ?? '';
  bannerState.voiVin = voiVin ?? '';
  bannerState.installationMode = installationMode ?? '';
  bannerState.tabHeaders = getTabHeaders(bannerState.tabList);
  bannerState.zIndex = zIndex ?? 2000;
  bannerState.inputVin = vin ?? '';
  bannerState.mileage = mileage ?? 0;
  bannerState.vin = vin ?? '';
};

const onStatesLoad = async (): Promise<void> => {
  if (bannerState.states.length === 0) {
    const stateData = await fetchStates();
    bannerState.states = stateData.map((state) => ({ label: state.abbreviation, value: state.abbreviation }));
  }
};

const saveCurrentStepToSessionStorage = (step: BannerStepStorage, data = null) => {
  if (step === BannerStepStorage.BannerCurrenStep) {
    writeStoreToBrowserStorage(
      {
        yearId: bannerState.yearId,
        model: bannerState.model,
        trim: bannerState.trim,
        vin: bannerState.vin,
        make: bannerState.make,
        mileage: bannerState.mileage,
        trimId: bannerState.trimId,
        vehiclePossibilities: bannerState.vehiclePossibilities,
        isActiveListing: bannerState.isActiveListing,
        activeDealerListingUrl: bannerState.activeDealerListingUrl,
        currentSelectedTab: bannerState.currentSelectedTab,
      },
      step,
      BROWSER_STORAGE_TTL.TTL1Hour
    );
  } else {
    writeStoreToBrowserStorage(data, step, BROWSER_STORAGE_TTL.TTL1Hour);
    const urlCurrentStep = getFromBrowserStorage(BannerStepStorage.UrlCurrentStep, false);
    const offerCertificate = getFromBrowserStorage(BannerStepStorage.OfferCertificate, false);

    if (offerCertificate) {
      setTtlFromBrowserStorage(BannerStepStorage.BannerCurrenStep, offerCertificate.ttl);
    } else if (urlCurrentStep) {
      setTtlFromBrowserStorage(BannerStepStorage.BannerCurrenStep, urlCurrentStep.ttl);
    }
  }
};

const resetSessionStorageSteps = () => {
  removeFromBrowserStorage(BannerStepStorage.BannerCurrenStep);
  removeFromBrowserStorage(BannerStepStorage.UrlCurrentStep);
  removeFromBrowserStorage(BannerStepStorage.OfferCertificate);
};

const checkSessionStorageToContinue = () => {
  try {
    const bannerCurrentStep = getFromBrowserStorage(BannerStepStorage.BannerCurrenStep, false);
    const urlCurrentStep = getFromBrowserStorage(BannerStepStorage.UrlCurrentStep, false);
    const offerCertificate = getFromBrowserStorage(BannerStepStorage.OfferCertificate, false);
    if (bannerCurrentStep) {
      bannerState.yearId = bannerCurrentStep.yearId;
      bannerState.vin = bannerCurrentStep.vin;
      bannerState.model = bannerCurrentStep.model;
      bannerState.make = bannerCurrentStep.make;
      bannerState.trim = bannerCurrentStep.trim;
      bannerState.mileage = bannerCurrentStep.mileage;
      bannerState.trimId = bannerCurrentStep.trimId;
      bannerState.vehiclePossibilities = bannerCurrentStep.vehiclePossibilities;
      bannerState.isActiveListing = bannerCurrentStep.isActiveListing;
      bannerState.activeDealerListingUrl = bannerCurrentStep.activeDealerListingUrl;
      bannerState.currentSelectedTab = bannerCurrentStep.currentSelectedTab;
    }
    if (offerCertificate) {
      removeFromBrowserStorage(BannerStepStorage.UrlCurrentStep);
      bannerState.offerCode = offerCertificate.offerCode;
      bannerState.markupAmounts = offerCertificate.markupAmounts;
      bannerState.totalOfferAmounts = offerCertificate.totalOfferAmounts;
      bannerState.status = offerCertificate.status;
      bannerState.amount = offerCertificate.amount;
      bannerState.expirationDate = offerCertificate.expirationDate;
      bannerState.offerId = offerCertificate.offerId;
      bannerState.activeStep = bannerStatus(offerCertificate);
    } else if (bannerCurrentStep || urlCurrentStep) {
      bannerState.activeStep = BannerStep.Continue;
    }
  } catch (error) {
    console.log(error);
  }
};

const continueSessionStorage = () => {
  trackBannerButtonClick(ACS_EVENT_PROPS.CONTINUE);
  try {
    const bannerCurrentStep = getFromBrowserStorage(BannerStepStorage.BannerCurrenStep, false);
    const urlCurrentStep = getFromBrowserStorage(BannerStepStorage.UrlCurrentStep, false);
    const offerCertificate = getFromBrowserStorage(BannerStepStorage.OfferCertificate, false);
    if (offerCertificate) {
      bannerState.activeStep = bannerStatus(offerCertificate);
    } else if (urlCurrentStep) {
      bannerState.activeStep = BannerStep.Continue;
      bannerState.isOpenModal = true;
      bannerState.furyUrl = urlCurrentStep.urlCurrentStep;
    } else if (bannerCurrentStep) {
      bannerState.activeStep = BannerStep.MSM;
      bannerState.yearId = bannerCurrentStep.yearId;
      bannerState.vin = bannerCurrentStep.vin;
      bannerState.model = bannerCurrentStep.model;
      bannerState.make = bannerCurrentStep.make;
      bannerState.trim = bannerCurrentStep.trim;
      bannerState.trimId = bannerCurrentStep.trimId;
      bannerState.mileage = bannerCurrentStep.mileage;
      bannerState.vehiclePossibilities = bannerCurrentStep.vehiclePossibilities;
      bannerState.isActiveListing = bannerCurrentStep.isActiveListing;
      bannerState.activeDealerListingUrl = bannerCurrentStep.activeDealerListingUrl;
      bannerState.currentSelectedTab = bannerCurrentStep.currentSelectedTab;
    }
  } catch (error) {
    console.log(error);
  }
};

const viewOfferCertificate = () => {
  trackBannerButtonClick(ACS_EVENT_PROPS.VIEW_OFFER_CERTIFICATE);
  window.open(createOfferCertificateUrl(bannerState.offerId, bannerState.offerCode));
};

const onFetchVehicleTypicalMileage = async (trimId: number) => {
  try {
    return await onFetchVehicleTypicalMileageSuccess(trimId);
  } catch (error) {
    onFetchVehicleTypicalMileageFail(error);
  }
};

const onFetchVehicleTypicalMileageSuccess = async (trimId: number) => {
  EventBus.publish('FetchVehicleTypicalMileage', {});
  bannerState.typicalMileage = await fetchVehicleTypicalMileage(trimId);
  EventBus.publish('FetchVehicleTypicalMileageSuccess', { trimId });
};

const onFetchVehicleTypicalMileageFail = (error) => {
  EventBus.publish('FetchVehicleTypicalMileageFail', error);
};

const onFetchYearsEligibility = () => {
  try {
    return onFetchYearsEligibilitySuccess();
  } catch (error) {
    onFetchYearsEligibilityFail(error);
  }
};

const onFetchYearsEligibilitySuccess = async () => {
  EventBus.publish('FetchYearsEligibility', {});
  if (Object.keys(bannerState.yearRange).length === 0) {
    bannerState.yearRange = await fetchYearsEligibility();
  }
  EventBus.publish('FetchYearsEligibilitySuccess', {});
};

const onFetchYearsEligibilityFail = (error) => {
  EventBus.publish('FetchYearsEligibilityFail', error);
};

const onTrackStartMyOffer = (valid) => {
  trackBannerButtonClick(ACS_EVENT_PROPS[`START_MY_OFFER${valid ? '' : '_ERROR'}`]);
};

const onTrackBannerReset = (textLink) => {
  const ascEvents = textLink.replace(/ /g, '_').toUpperCase();
  trackBannerButtonClick(ACS_EVENT_PROPS[ascEvents]);
};

const onBannerDisconnected = () => {
  onShiftDigitalRemove();
};

export const onMessage = (event) => {
  if (typeof event?.data === 'string') {
    try {
      const data = JSON.parse(event.data);
      if (data.urlCurrentStep) {
        const icoPath = data.urlCurrentStep.charAt(0) === '/' ? '/instant-cash-offer' : '/instant-cash-offer/';
        data.urlCurrentStep = `${event.origin}${icoPath}${data.urlCurrentStep}`;
        saveCurrentStepToSessionStorage(BannerStepStorage.UrlCurrentStep, data);
      } else if (data.offerCertificate) {
        saveCurrentStepToSessionStorage(BannerStepStorage.OfferCertificate, data.offerCertificate);
      }
    } catch (error) {
      EventBus.publish('onMessage', error);
    }
  }
};

const actions = {
  onOptimizelyCheck,
  onHealthCheck,
  onBannerNextAction,
  onModalClose,
  onCheckInit,
  onDealerAuthorizedCheckInit,
  onDealerInCobrandingGroupCheckInit,
  onFindVehicleByYMM,
  checkVehicleEligibility,
  onFindVehicleByLP,
  resetBannerState,
  onFindByVin,
  onYmmSuggest,
  onYmmSelected,
  onMileageSubmit,
  setCurrentSelectedTab,
  updateVinInputStatus,
  updateLicensePlateStatus,
  updateYMMStatusThenValidate,
  updateMileageStatus,
  onReceivedParams,
  onStatesLoad,
  saveCurrentStepToSessionStorage,
  resetSessionStorageSteps,
  checkSessionStorageToContinue,
  continueSessionStorage,
  viewOfferCertificate,
  onFetchYearsEligibility,
  onBannerDisconnected,
  onYmmChange,
  onTrackStartMyOffer,
  onTrackBannerReset,
  onMessage,
  onFetchVehicleTypicalMileage,
};

export { actions };
export default actions;
